/**
 * In-browser structural validation that a parsed CEL expression is a usable
 * rule *condition* — i.e. it evaluates to a boolean. Mirrors the backend
 * {@code CelRuleConditionParser} Walker's condition-position rule: only a
 * boolean-producing call (comparison, &&/||/!, string method, macro) or a
 * comprehension is a valid condition; a bare select / ident / list / literal
 * at condition position is rejected, and &&/||/! operands are checked
 * recursively.
 *
 * <p>This is pure AST shape — no type information — so it runs against the
 * @bufbuild/cel parse output without a type checker. It catches the issue-999
 * class (`<bool> && identity.attributes.<attr>`, bare `identity`) instantly in
 * the browser; the backend `/validate-expression` endpoint stays authoritative
 * for the full type-check + row-reference ACL.
 */

import {parse as celParse} from '@bufbuild/cel'
import type {Expr} from '@bufbuild/cel-spec/cel/expr/syntax_pb.js'
import type {ParsedExpr} from '@bufbuild/cel-spec/cel/expr/syntax_pb.js'

const AND = '_&&_'
const OR = '_||_'
const NOT = '!_'

/** Shared prefix both engines emit (backend appends the AST kind, e.g. `, got SELECT`). */
export const NOT_A_BOOLEAN_CONDITION = 'Expected boolean expression at top level'

/**
 * Returns a rejection message when the expression is not a boolean condition,
 * or {@code null} when its top-level shape is acceptable. Recurses into
 * `&&` / `||` / `!` operands.
 */
export function ruleConditionShapeError(expr: Expr | undefined): string | null {
    const kind = expr?.exprKind
    if (!kind) return NOT_A_BOOLEAN_CONDITION
    switch (kind.case) {
        case 'callExpr': {
            const fn = kind.value.function
            if (fn === AND || fn === OR) {
                for (const arg of kind.value.args ?? []) {
                    const r = ruleConditionShapeError(arg)
                    if (r !== null) return r
                }
                return null
            }
            if (fn === NOT) return ruleConditionShapeError(kind.value.args?.[0])
            return null
        }
        case 'comprehensionExpr':
            return null
        default:
            return NOT_A_BOOLEAN_CONDITION
    }
}

/** Convenience over a full {@link ParsedExpr}. */
export function ruleConditionError(parsed: ParsedExpr): string | null {
    return ruleConditionShapeError(parsed.expr)
}

/**
 * Instant in-browser structural check over a raw expression string, for the
 * editor's inline feedback. Returns a rejection message for the structural
 * not-a-condition class, or {@code null} when the shape is acceptable.
 *
 * Syntax errors return {@code null} on purpose: @bufbuild/cel's parse-error
 * wording differs from cel-java's, so we defer syntax + type + ACL verdicts to
 * the authoritative backend `/validate-expression` rather than voice a
 * divergent message in the browser. This check only adds an EARLY, definite
 * "not a boolean condition" signal — it never declares an expression valid.
 */
export function inBrowserRuleConditionError(expression: string): string | null {
    let parsed
    try {
        parsed = celParse(expression)
    } catch {
        return null
    }
    return ruleConditionError(parsed)
}
